import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import VatRateSelect from '../components/VatRateSelect';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { name: '', price: '', vat_rate: 0.085 };

const Prestations = () => {
  const confirm = useConfirm();
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchPrestations(); }, []);

  const fetchPrestations = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/prestations`, { withCredentials: true });
      setPrestations(r.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name.trim(),
      price: parseFloat(formData.price),
      vat_rate: parseFloat(formData.vat_rate),
    };
    if (!payload.name || isNaN(payload.price)) {
      toast.error('Type et prix obligatoires');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/prestations/${editing.id}`, payload, { withCredentials: true });
        toast.success('Prestation mise à jour');
      } else {
        await axios.post(`${API_URL}/api/prestations`, payload, { withCredentials: true });
        toast.success('Prestation créée');
      }
      setShowDialog(false);
      setFormData(emptyForm);
      setEditing(null);
      fetchPrestations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Supprimer cette prestation ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/prestations/${id}`, { withCredentials: true });
      toast.success('Prestation supprimée');
      fetchPrestations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditDialog = (p) => {
    setEditing(p);
    setFormData({
      name: p.name || '',
      price: (p.price ?? '').toString(),
      vat_rate: p.vat_rate ?? 0.085,
    });
    setShowDialog(true);
  };

  const filtered = prestations.filter((p) =>
    !searchQuery || (p.name || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="prestations-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Prestations de service</h1>
            <p className="text-slate-600">Gérez vos prestations facturables (type + prix)</p>
          </div>
          <Button
            onClick={() => { setEditing(null); setFormData(emptyForm); setShowDialog(true); }}
            data-testid="create-prestation-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" /> Nouvelle prestation
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement…</div>
        ) : (
          <>
            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par type de prestation…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-prestations-input"
                className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Type de prestation</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix TTC</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">TVA</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix HT (calculé)</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => {
                    const vat = p.vat_rate ?? 0.085;
                    const priceTtc = Number(p.price || 0);
                    const priceHt = priceTtc / (1 + vat);
                    return (
                    <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{p.name}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 text-right font-semibold">{priceTtc.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-600 text-right">{(vat * 100).toFixed(1)} %</td>
                      <td className="py-3 px-4 text-sm text-slate-500 text-right">{priceHt.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openEditDialog(p)}
                          data-testid={`edit-prestation-${p.id}`}
                          className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-2"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          data-testid={`delete-prestation-${p.id}`}
                          className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md"
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  );})}
                  {filtered.length === 0 && (
                    <tr><td colSpan="5" className="py-12 text-center text-slate-500">Aucune prestation enregistrée</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent data-testid="prestation-dialog" className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Nouvelle'} prestation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type de prestation *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="prestation-name-input"
                  placeholder="Ex : Dépannage informatique sur site"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prix TTC (€) *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  data-testid="prestation-price-input"
                  placeholder="Prix toutes taxes comprises"
                  required
                />
                {formData.price && !isNaN(parseFloat(formData.price)) && (
                  <p className="text-xs text-slate-500 mt-1">
                    Prix HT calculé : <strong>{(parseFloat(formData.price) / (1 + (parseFloat(formData.vat_rate) || 0.085))).toFixed(2)} €</strong> (avec {((parseFloat(formData.vat_rate) || 0.085) * 100).toFixed(1)} % de TVA)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Taux de TVA</label>
                <VatRateSelect
                  value={formData.vat_rate}
                  onChange={(v) => setFormData({ ...formData, vat_rate: v })}
                  testId="prestation-vat-rate-select"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Le prix saisi est en TTC. Lors de la facturation, le montant HT et la TVA sont calculés automatiquement.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} data-testid="cancel-prestation-btn">
                  Annuler
                </Button>
                <Button type="submit" data-testid="submit-prestation-btn" className="bg-blue-600 hover:bg-blue-500 text-white">
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Prestations;
